import React, { useMemo } from 'react';
import { Card, Col, Flex, Row, Typography, Timeline } from 'antd';
import { startCase } from 'lodash';
import dayjs from '../../../dayjs';
import './BookingTemplate.css';
import { DATE_STRING } from '../../../model/schedule';
import { secsSinceMidnightToDayjs } from '../../../model/timeFilter';
import { useAppContext } from '../../../libs/contextLib';
import config from '../../../config';
import Pin from '../../../components/Pin';
import VerticalTimeline from '../../../components/VerticalTimeline';
import CBLConditions from './CBLConditions';
import useAllVehicleTypes from '../../../hooks/useAllVehicleType';
const { Paragraph, Text } = Typography;

const ConfirmationTemplate = ({ charter, allSchedules, operator, bookings = false, allCharterSchedules }) => {    
    const { allVehicleTypes } = useAllVehicleTypes();
    
    const stopBreakdown = useMemo(() => {
        if (!charter?.itinerary?.length) {
            return [];
        }

        const times = []

        charter.itinerary.forEach(itinerary => {
            if (itinerary.stops.length > 0) {
                times.push({
                    firstStop: itinerary.stops[0],
                    firstStopTime: itinerary.stops[0]?.departSecs || null,
                    lastStop: itinerary.stops[itinerary.stops.length - 1],
                    lastStopTime: itinerary.stops[itinerary.stops.length - 1]?.departSecs || null,
                    middleStopsCount: itinerary.stops.length > 2 ? itinerary.stops.length - 2 : 0
                })
            }
        })

        return times
    }, [charter.itinerary]);

    // Calculate vehicle types for summary
    const vehicleTypesSummary = useMemo(() => {
        if (!charter?.itinerary?.length || !allVehicleTypes) {
            return [];
        }

        const vehicleTypeCounts = {};
        
        charter.itinerary.forEach(leg => {
            leg.services?.forEach(service => {
                if (service.vehicleTypes && service.vehicleTypeQuantities) {
                    service.vehicleTypes.forEach(vehicleTypeId => {
                        const quantity = service.vehicleTypeQuantities[vehicleTypeId] || 1;
                        const vehicleType = allVehicleTypes[vehicleTypeId];
                        if (vehicleType) {
                            const key = vehicleType.vehicleTypeName;
                            vehicleTypeCounts[key] = (vehicleTypeCounts[key] || 0) + quantity;
                        }
                    });
                }
            });
        });

        return Object.entries(vehicleTypeCounts).map(([name, count]) => ({
            name,
            count
        }));
    }, [charter.itinerary, allVehicleTypes]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value || 0);
    };

    if (!charter) {
        return <div>No charter data available</div>;
    }

    let startDate = charter.startDate;
    let startTime = charter.startTime;

    if (bookings) {
        startDate = charter.dates?.[0] || charter.startDate || 'TBC';
        const earliestStartTime = charter.itinerary.reduce((earliest, itinerary) => {
            itinerary.services.forEach(service => {
                const startTime = service.startTimeAsSecsSinceMidnight
                if (startTime < earliest) {
                    earliest = startTime;
                }
            });
            return earliest;
        }, Infinity);
        if (earliestStartTime === Infinity) {
            startTime = null;
        } else {
            startTime = earliestStartTime;
        }
    } else {
        if (!charter?.duties?.length) {
            startDate = charter.startDate;
            startTime = charter.startTime;
        } else {
            const firstDuty = charter.duties[0];
            const firstServiceRow = firstDuty.rows.find(row => row.type === 'Service');
            startDate = firstDuty?.getRunningDates(allSchedules, dayjs())?.[0];
            startTime = firstServiceRow?.time;
        }
    }

    const logo = `https://${config.s3.BUCKET}.s3.ap-southeast-2.amazonaws.com/public/${operator?.operatorId}/logo.png?${Date.now()}`

    const quoteLineItems = charter.quoteLineItems || [];
    
    // Determine travel dates or schedule text based on charter type
    const isContractBooking = charter.charterType === "recurring";
    const scheduleName = isContractBooking && charter.scheduleId && allCharterSchedules ? allCharterSchedules[charter.scheduleId]?.scheduleName : null;

    return (
        <div className="print-pdf" style={{paddingTop: 0}}>
            <div className="quote-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', paddingBottom: '10px', borderBottom: '2px solid #EFEDFD' }}>
                <div style={{ maxWidth: '60%' }}>
                    <div className="quote-title-section">
                        <h1 style={{ fontSize: 32, fontWeight: 600, color: '#333', marginBottom: 10 }}>BOOKING CONFIRMATION</h1>
                        <h2 className="pdf-title">{startCase(charter.name || '')}</h2>
                        <div className="quote-meta">
                            <div className="quote-number" style={{minWidth: '75px'}}>
                                <label>Booking Number</label>
                                <strong>{startCase(charter.ref || charter.bookingReference || '')}</strong>
                            </div>
                            <div className="quote-date">
                                <label>Booking Date</label>
                                <strong>{charter.quoteDate?.format ? charter.quoteDate.format(DATE_STRING) : dayjs().format(DATE_STRING)}</strong>
                            </div>
                            {charter.quoteDescription && (
                                <div className="quote-description">
                                    <label>Booking Description</label>
                                    <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.4' }}>
                                        {(charter.quoteDescription || '').split('\n').map((p, index) =>
                                            <div key={`desc-${index}`} style={{ marginBottom: index < charter.quoteDescription.split('\n').length - 1 ? '4px' : '0' }}>
                                                {p}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {
                    operator &&
                    (<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '20px', maxWidth: '40%' }}>
                        <div style={{ textAlign: 'right' }}>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#444', marginBottom: '5px' }}>{operator.operatorName}</p>
                            <p style={{ margin: 0, fontSize: '12px', color: '#666', lineHeight: 1.4 }}>{operator.operatorPostalAddress}</p>
                            <p style={{ margin: 0, fontSize: '12px', color: '#666', lineHeight: 1.4 }}>{operator.operatorPhone}</p>
                            <p style={{ margin: 0, fontSize: '12px', color: '#666', lineHeight: 1.4 }}>{operator.operatorEmail}</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '140px' }}>
                            <img id="operator-logo" src={logo}
                                style={{ width: '120px', height: 'auto', objectFit: 'contain'}}
                                alt={operator.operatorName}
                            />
                        </div>
                    </div>)
                }
            </div>

            <div className="quote-content">
                <Row gutter={[20, 15]}>
                    <Col xs={24}>
                        <Card title={''} className="card-main card-info">
                            <Row gutter={[20, 20]}>
                                <Col xs={24} sm={7}>
                                    <label>To</label>
                                    <strong>{startCase(charter.customer?.name || '')}</strong>
                                    <strong>{startCase(charter?.address1 || '')}</strong>
                                    <strong>{startCase(charter?.address2 || '')}</strong>
                                    <strong>{charter?.addressContact || ''}</strong>
                                </Col>
                                <Col xs={24} sm={12}>
                                    {isContractBooking ? (
                                        <>
                                            <label>Travel schedule</label>
                                            <strong>{scheduleName || "TBC"}</strong>
                                        </>
                                    ) : (
                                        <>
                                            <label>Travel dates</label>
                                            <span style={{fontWeight: 400, fontSize: 14}}>
                                                {charter.dates && charter.dates.length > 0 ? 
                                                    charter.dates.map(date => 
                                                        date?.format ? date.format(DATE_STRING) : ''
                                                    ).join(', ')
                                                    : (startDate?.format ? startDate.format(DATE_STRING) : 'TBC')
                                                }
                                            </span>
                                        </>
                                    )}
                                </Col>
                                <Col xs={12} sm={5}>
                                    <label>Passengers</label>
                                    <strong style={{ marginBottom: '10px' }}>{charter.passengerCount || 0}</strong>
                                    {vehicleTypesSummary.length > 0 && (
                                        <>
                                        <label>Vehicle Types</label>
                                         <div>
                                             {vehicleTypesSummary.map((vehicle, index) => (
                                                 <div key={index}>
                                                     <strong>{vehicle.count}x {startCase(vehicle.name)}</strong>
                                                 </div>
                                             ))}
                                         </div>
                                        </>
                                    )}
                                </Col>
                                {charter.note && 
                                    <Col xs={24}>
                                        <label>Additional Booking Details</label>
                                        <small>{(charter.note || '').split('\n').map((p, index) =>
                                            <Paragraph key={`note-${index}`} style={{ marginBottom: '0' }}>{p}</Paragraph>)}
                                        </small>
                                    </Col>
                                }
                            </Row>
                        </Card>
                    </Col>
                    <Col xs={24}>
                        <Card bordered={false} className="card-main pa-0">
                            <Row className="card-row-header align-items-center">
                                <Col xs={4} className="text-left">
                                    <span>NAME</span>
                                </Col>
                                <Col xs={8} className="text-left">
                                    <span>DESCRIPTION</span>
                                </Col>
                                <Col xs={3} className="text-right">
                                    <span>PRICE</span>
                                </Col>
                                <Col xs={3} className="text-center">
                                    <span>QTY</span>
                                </Col>
                                <Col xs={3} className="text-center">
                                    <span>DISCOUNT</span>
                                </Col>
                                <Col xs={3} className="text-right">
                                    <span>AMOUNT</span>
                                </Col>
                            </Row>
                            {quoteLineItems.map((line, index) => (
                                <Row key={`qline-${index}`} className="card-row-content border-full align-items-center">
                                    <Col xs={4} className="text-left">
                                        <strong>{startCase(line.name || '')}</strong>
                                    </Col>
                                    <Col xs={8} className="text-left">
                                        <span>{line.description || ''}</span>
                                    </Col>
                                    <Col xs={3} className="text-right">
                                        <span>{formatCurrency(line.unitPrice || 0)}</span>
                                    </Col>
                                    <Col xs={3} className="text-center">
                                        <span>{line.quantity || 0}</span>
                                    </Col>
                                    <Col xs={3} className="text-center">
                                        <span>{formatCurrency(line.discount || 0)}</span>
                                    </Col>
                                    <Col xs={3} className="text-right">
                                        <span>{formatCurrency(charter.calculateLineItemAmount(line))}</span>
                                    </Col>
                                </Row>
                            ))}
                            <Row className="card-row-content border-full align-items-center totals-row">
                                <Col xs={16}>
                                </Col>
                                <Col xs={4} className="text-right totals-label">
                                    <strong>Line Item Total</strong>
                                </Col>
                                <Col xs={4} className="text-right">
                                    <strong>{formatCurrency(charter.calculateSubTotal())}</strong>
                                </Col>
                            </Row>
                            <Row className="card-row-content border-full align-items-center totals-row">
                                <Col xs={16}>
                                </Col>
                                <Col xs={4} className="text-right totals-label">
                                    <strong>SUBTOTAL</strong>
                                </Col>
                                <Col xs={4} className="text-right">
                                    <strong>{formatCurrency(charter.calculateTotalAfterDiscount())}</strong>
                                </Col>
                            </Row>
                            <Row className="card-row-content border-full align-items-center totals-row">
                                <Col xs={16}>
                                </Col>
                                <Col xs={4} className="text-right totals-label">
                                    <strong>GST (10%)</strong>
                                </Col>
                                <Col xs={4} className="text-right">
                                    <strong>{formatCurrency(charter.calculateGST())}</strong>
                                </Col>
                            </Row>
                            <Row className="card-row-content border-full align-items-center totals-row final-total">
                                <Col xs={16}>
                                </Col>
                                <Col xs={4} className="text-right totals-label">
                                    <strong>TOTAL</strong>
                                </Col>
                                <Col xs={4} className="text-right">
                                    <strong>{formatCurrency(charter.calculateTotal())}</strong>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                    <Col xs={24}>
                        <Card title={'Payment Details'} className="card-main card-info">
                            <Row gutter={[20, 20]}>
                                <Col xs={24} sm={8}>
                                    <label>Bank Name</label>
                                    <strong>{operator?.bankDetails?.bankName || '-'}</strong>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <label>Account Name</label>
                                    <strong>{operator?.bankDetails?.accountName || '-'}</strong>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <label>BSB</label>
                                    <strong>{operator?.bankDetails?.bsb || '-'}</strong>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <label>Account Number</label>
                                    <strong>{operator?.bankDetails?.accountNumber || '-'}</strong>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <label>Australian Business Number</label>
                                    <strong>{operator?.bankDetails?.abn || '-'}</strong>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                    {/* Charter Itinerary */}
                    <Col xs={24}>
                        <Card title={'Charter Itinerary'} className="card-main card-info">
                            <Row gutter={[20, 15]}>
                                <Col xs={24}>
                                    {charter.itinerary.map((itinerary, index) => {
                                        const hasMultipleTrips = itinerary.services && itinerary.services.length > 1;
                                        
                                        // Prepare stops for vertical timeline
                                        const stops = itinerary.stops.map((stop, stopIndex) => {
                                            // Get charter stop type from stopTime, not stop
                                            const stopTime = itinerary.services[0]?.stopTimes?.find(st => st.stopTimeId === stop.stopTimeId);
                                            const charterStopType = stopTime?.charterStopType || (stopIndex === 0 ? 'pickup' : stopIndex === itinerary.stops.length - 1 ? 'dropoff' : 'pickup');
                                            
                                            if (hasMultipleTrips) {
                                                // For multi-trip: get all times for this stop across all services
                                                const stopTimes = itinerary.services.map(service => {
                                                    const stopTime = service.stopTimes[stopIndex]
                                                    return stopTime?.departSecs || service.startTimeAsSecsSinceMidnight;
                                                }).filter(time => time); // Remove undefined times
                                                
                                                return {
                                                    id: stop.stopId,
                                                    name: stop.stopName,
                                                    time: stopTimes.length > 0 ? 
                                                        stopTimes.map(time => secsSinceMidnightToDayjs(time || 0)?.format('HH:mm')).join('\n') : 
                                                        'TBC',
                                                    isFirst: stopIndex === 0,
                                                    isLast: stopIndex === itinerary.stops.length - 1,
                                                    charterStopType: charterStopType,
                                                    notes: stopTime?.notes || stop.notes || null
                                                };
                                            } else {
                                                // For single trip: use the first service
                                                const arriveTime = stopTime?.arriveSecs || 0;
                                                const departTime = stopTime?.departSecs || itinerary.services[0]?.startTimeAsSecsSinceMidnight || 0;
                                                
                                                // Format time based on stop type
                                                let timeDisplay = '';
                                                
                                                if (charterStopType === 'both' && arriveTime !== departTime && arriveTime > 0 && departTime > 0) {
                                                    // Show both times if they're different and both are set
                                                    timeDisplay = `${secsSinceMidnightToDayjs(arriveTime)?.format('HH:mm')} - ${secsSinceMidnightToDayjs(departTime)?.format('HH:mm')}`;
                                                } else if (charterStopType === 'dropoff' && arriveTime > 0) {
                                                    // Show arrival time for dropoff stops
                                                    timeDisplay = secsSinceMidnightToDayjs(arriveTime)?.format('HH:mm') || 'TBC';
                                                } else {
                                                    // Show departure time for pickup stops or default
                                                    timeDisplay = secsSinceMidnightToDayjs(departTime)?.format('HH:mm') || 'TBC';
                                                }
                                                
                                                return {
                                                    id: stop.stopId,
                                                    name: stop.stopName,
                                                    time: timeDisplay,
                                                    isFirst: stopIndex === 0,
                                                    isLast: stopIndex === itinerary.stops.length - 1,
                                                    charterStopType: charterStopType,
                                                    notes: stopTime?.notes || stop.notes || null
                                                };
                                            }
                                        });

                                        return (
                                            <div key={`itinerary-${index}`} style={{
                                                marginBottom: '20px',
                                                borderRadius: '8px',
                                                border: '1px solid #dee2e6',
                                                overflow: 'hidden'
                                            }}>
                                                {/* Header: Charter Title + Trip Info */}
                                                <div style={{
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '12px 16px',
                                                    borderBottom: '1px solid #dee2e6'
                                                }}>
                                                    <Row gutter={[16, 0]} align="middle">
                                                        <Col xs={24} md={16}>
                                                            <div style={{
                                                                fontSize: '16px',
                                                                fontWeight: 600,
                                                                color: '#2c3e50',
                                                                marginBottom: '-4px'
                                                            }}>
                                                                {startCase(itinerary.routeName || `Charter ${index + 1}`)}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '13px',
                                                                color: '#6c757d'
                                                            }}>
                                                                {hasMultipleTrips ? `${itinerary.services.length} trips` : 'Single trip'}
                                                            </div>
                                                        </Col>
                                                        <Col flex="auto">
                                                            {/* Vehicle Types for this itinerary */}
                                                            {itinerary.services && itinerary.services.length > 0 && (() => {
                                                                const vehicleTypes = [];
                                                                itinerary.services.forEach(service => {
                                                                    if (service.vehicleTypes && service.vehicleTypeQuantities && allVehicleTypes) {
                                                                        service.vehicleTypes.forEach(vehicleTypeId => {
                                                                            const quantity = service.vehicleTypeQuantities[vehicleTypeId] || 1;
                                                                            const vehicleType = allVehicleTypes[vehicleTypeId];
                                                                            if (vehicleType) {
                                                                                const existingVehicle = vehicleTypes.find(v => v.name === vehicleType.vehicleTypeName);
                                                                                if (existingVehicle) {
                                                                                    existingVehicle.count += quantity;
                                                                                } else {
                                                                                    vehicleTypes.push({
                                                                                        name: vehicleType.vehicleTypeName,
                                                                                        count: quantity
                                                                                    });
                                                                                }
                                                                            }
                                                                        });
                                                                    }
                                                                });
                                                                
                                                                return vehicleTypes.length > 0 ? (
                                                                    <div>
                                                                        <div style={{
                                                                            fontSize: '12px',
                                                                            color: '#6c757d',
                                                                            lineHeight: '1'
                                                                        }}>
                                                                            Vehicle Types
                                                                        </div>
                                                                        <div style={{
                                                                            fontSize: '13px',
                                                                            fontWeight: 600,
                                                                            color: '#2c3e50'
                                                                        }}>
                                                                            {vehicleTypes.map((vehicle, vehicleIndex) => (
                                                                                <span key={vehicleIndex}>
                                                                                    {vehicle.count}x {startCase(vehicle.name)}
                                                                                    {vehicleIndex < vehicleTypes.length - 1 && ', '}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ) : null;
                                                            })()}
                                                        </Col>
                                                    </Row>
                                                </div>

                                                {/* Vertical Timeline */}
                                                <div style={{
                                                    backgroundColor: 'white',
                                                    padding: '16px 0'
                                                }}>
                                                    <VerticalTimeline stops={stops} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </div>
            {operator?.opts?.charter?.conditionsPrefix === "cbl" && <CBLConditions />}
        </div>
    );
};

export default React.memo(ConfirmationTemplate);